/**
 * TableEditor — render shell for `create-table` / `alter-table`
 * `DdlStructure`. Tabs (Columns / Constraints / Indexes / Storage) host the
 * per-facet scaffolding; affordances light up only when an `editContext` is
 * supplied.
 *
 * Two variants:
 *  - `'full'` (default) — the Table Designer workspace tab: header line,
 *    dependency sidebar rail, roomy tab density.
 *  - `'compact'` — hosted inside the compact Visual DDL popover: no sidebar,
 *    count-labelled tab triggers, tightened per-tab density, and the
 *    lifecycle actions (Rename / Move to schema) folded into the tab-strip row
 *    with `ml-auto` instead of occupying a right-aligned row of their own above
 *    it (which zig-zagged against the left-aligned tabs over a dead left half).
 *    The dependency hook + blast-radius gate wiring is IDENTICAL in both
 *    variants (the lifecycle popup needs the gate even without the sidebar
 *    rail).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 — Read-only Render Scaffolding.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/
 */
import { useMemo, useState } from 'react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../../../components/ui/tabs';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type { DdlStructure, IndexDef } from '../../../../sql-resolver/types/ddlEdit';
import { buildBlastRadiusGate } from '../../dependencies/buildBlastRadiusGate';
import { useDependencyAnnotations } from '../../dependencies/useDependencyAnnotations';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { useVqeDdlStore } from '../../store';
import { DdlEditorShell, type DdlEditorShellVariant } from '../common/DdlEditorShell';
import { ColumnsTab } from './ColumnsTab';
import { ConstraintsTab } from './ConstraintsTab';
import { IndexesTab } from './IndexesTab';
import { StorageTab } from './StorageTab';
import { TableEditorSidebar } from './TableEditorSidebar';
import { compactColumnsTabLabel, tableFacetCounts } from './tableFacetCounts';
import { TableLifecycleHeaderActions } from './TableLifecycleHeaderActions';

type TableTab = 'columns' | 'constraints' | 'indexes' | 'storage';

type TabLabels = Readonly<Record<TableTab, string>>;

const FULL_TAB_LABELS: TabLabels = {
  columns: 'Columns',
  constraints: 'Constraints',
  indexes: 'Indexes',
  storage: 'Storage',
};

function formatQualifiedName(q: QualifiedName | undefined | null): string | null {
  if (!q) return null;
  return q.raw && q.raw.length > 0 ? q.raw : q.name;
}

function tableNameFromStructure(structure: DdlStructure | null): string | null {
  if (!structure) return null;
  if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
    return formatQualifiedName(structure.table);
  }
  if (structure.kind === 'create-index' || structure.kind === 'drop-index') {
    return formatQualifiedName(structure.kind === 'create-index' ? structure.index.table : structure.table);
  }
  return null;
}

function tableQualifiedNameFromStructure(structure: DdlStructure | null): QualifiedName | null {
  if (!structure) return null;
  if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
    return structure.table;
  }
  return null;
}

function kindLabelFromStructure(structure: DdlStructure | null): string {
  if (!structure) return 'Table';
  switch (structure.kind) {
    case 'create-table':
      return 'CREATE TABLE';
    case 'alter-table':
      return 'ALTER TABLE';
    case 'create-index':
      return 'CREATE INDEX';
    case 'drop-index':
      return 'DROP INDEX';
    default:
      return 'Table';
  }
}

export interface TableEditorProps {
  readonly structure: DdlStructure | null;
  readonly onEditAsSql: () => void;
  /** Optional override of the initial active tab. Defaults to `'columns'`. */
  readonly initialTab?: TableTab;
  /** Required `data-testid` prefix for unit + E2E specs. */
  readonly testId?: string;
  /** Optional edit context — when supplied, per-tab edit affordances light up
   *  per their corresponding Phase A–E slices. When null/undefined, the editor
   *  stays read-only (Slice 0.4 scaffolding). */
  readonly editContext?: VqeDdlEditContext | null;
  /** `'full'` = Table Designer workspace tab (default); `'compact'` = the
   *  compact Visual DDL popover body (no sidebar, count-labelled tabs,
   *  tightened density). */
  readonly variant?: DdlEditorShellVariant;
  /** The indexes of the CREATE INDEX statements that follow `structure` in
   *  the same document (the Table Designer's buffer). Counted on the Indexes
   *  tab label and listed by `IndexesTab`. */
  readonly indexes?: readonly IndexDef[];
}

export function TableEditor({
  structure,
  onEditAsSql,
  initialTab = 'columns',
  testId = 'ddl-table-editor',
  editContext,
  variant = 'full',
  indexes,
}: TableEditorProps) {
  const [tab, setTab] = useState<TableTab>(initialTab);
  const compact = variant === 'compact';
  const objectName = tableNameFromStructure(structure);
  const kindLabel = kindLabelFromStructure(structure);
  const fromTable = tableQualifiedNameFromStructure(structure);
  const showLifecycleActions =
    !!editContext && (structure?.kind === 'create-table' || structure?.kind === 'alter-table');

  // Slice H2: always-on dependency annotations. The hook's `enabled` gate
  // keeps the IPC quiet until every required field is present (connection
  // bound + table named); no-op when the editor is parsed-only.
  const dep = useDependencyAnnotations({
    connectionId: editContext?.connectionId,
    database: editContext?.database,
    schema: fromTable?.schema ?? null,
    objectName: fromTable?.name ?? null,
    objectType: 'table',
    enabled: showLifecycleActions,
  });
  const sidebarMode: 'connected' | 'disconnected' =
    editContext?.connectionId && editContext?.database ? 'connected' : 'disconnected';

  // Slice H3: derive a per-edit blast-radius gate from the live dependency
  // snapshot. The Designer host's commitDdlEdit drop ops route through this
  // gate so the user confirms the BlastRadiusPanel before any DROP commits.
  const annotations = dep.annotations;
  const setPendingBlastRadius = useVqeDdlStore((s) => s.setPendingBlastRadius);
  const editContextWithGate = useMemo<VqeDdlEditContext | null>(() => {
    if (!editContext || !showLifecycleActions) return editContext ?? null;
    return {
      ...editContext,
      blastRadius: buildBlastRadiusGate({
        source: 'designer',
        annotations,
        annotationsReady: dep.isSuccess,
        setPendingBlastRadius,
        target: {
          schema: fromTable?.schema ?? null,
          objectName: fromTable?.name ?? null,
        },
      }),
    };
  }, [editContext, showLifecycleActions, setPendingBlastRadius, annotations, dep.isSuccess, fromTable?.schema, fromTable?.name]);

  // Compact tab triggers carry a glanceable count so the popover's tab strip
  // says what each facet holds without opening it — but ONLY for the facets
  // whose data is real. `Constraints` / `Storage` have no structured list in
  // `DdlStructure`, so a count there would always read `(0)` and lie about the
  // object; they stay plain labels. On an ALTER TABLE the Columns tab lists
  // only the changed columns, so its label names the change
  // (`compactColumnsTabLabel`). The full variant keeps its bare labels
  // (E2E specs locate tabs by testid, but the label text is part of the
  // workspace tab's established look).
  const counts = useMemo(() => tableFacetCounts(structure, indexes), [structure, indexes]);
  const labels: TabLabels = compact
    ? {
        columns: compactColumnsTabLabel(structure),
        constraints: 'Constraints',
        indexes: `Indexes (${counts.indexes})`,
        storage: 'Storage',
      }
    : FULL_TAB_LABELS;
  const tabContentClass = compact ? 'pt-2' : 'pt-4';
  // Compact: the underline moves onto the tab-strip ROW so it still spans the
  // full width once the lifecycle actions share that row (see F15 above).
  const tabsListClass = compact ? 'h-7 gap-0.5 border-b-0' : undefined;
  const tabTriggerClass = compact ? 'px-2 py-1 text-xs' : undefined;

  const lifecycleActions =
    showLifecycleActions && editContextWithGate ? (
      <TableLifecycleHeaderActions
        editContext={editContextWithGate}
        fromTable={fromTable}
        testId={`${testId}-lifecycle`}
        iconOnly={compact}
      />
    ) : null;

  return (
    <DdlEditorShell
      kindLabel={kindLabel}
      objectName={objectName}
      testId={testId}
      variant={variant}
      onEditAsSql={onEditAsSql}
      readOnly={!editContext}
      headerActions={compact ? null : lifecycleActions}
      sidebar={
        compact ? undefined : (
          <TableEditorSidebar
            annotations={dep.annotations}
            isLoading={dep.isLoading}
            error={dep.error}
            mode={sidebarMode}
            schema={fromTable?.schema ?? null}
            objectName={fromTable?.name ?? null}
            kindLabel={kindLabel}
            connectionId={editContext?.connectionId}
            database={editContext?.database}
            showLifecycleActions={showLifecycleActions}
            testId={testId}
          />
        )
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as TableTab)}>
        <div
          // `flex-wrap` is the graceful degradation for a user-narrowed
          // popover: the actions drop to a second (still right-aligned) line
          // instead of clipping off the edge.
          className={
            compact
              ? 'flex flex-wrap items-center gap-2 border-b border-border'
              : undefined
          }
        >
          <TabsList className={tabsListClass} data-testid={`${testId}-tabs`}>
            <TabsTrigger
              value="columns"
              className={tabTriggerClass}
              data-testid={`${testId}-tab-columns`}
            >
              {labels.columns}
            </TabsTrigger>
            <TabsTrigger
              value="constraints"
              className={tabTriggerClass}
              data-testid={`${testId}-tab-constraints`}
            >
              {labels.constraints}
            </TabsTrigger>
            <TabsTrigger
              value="indexes"
              className={tabTriggerClass}
              data-testid={`${testId}-tab-indexes`}
            >
              {labels.indexes}
            </TabsTrigger>
            <TabsTrigger
              value="storage"
              className={tabTriggerClass}
              data-testid={`${testId}-tab-storage`}
            >
              {labels.storage}
            </TabsTrigger>
          </TabsList>
          {compact && lifecycleActions ? (
            <div
              className="ml-auto flex items-center gap-1"
              data-testid={`${testId}-header-actions`}
            >
              {lifecycleActions}
            </div>
          ) : null}
        </div>

        <TabsContent value="columns" className={tabContentClass}>
          <ColumnsTab
            structure={structure}
            testId={`${testId}-columns`}
            editContext={editContextWithGate}
            dependencyAnnotations={dep.annotations}
            compact={compact}
          />
        </TabsContent>
        <TabsContent value="constraints" className={tabContentClass}>
          <ConstraintsTab
            testId={`${testId}-constraints`}
            editContext={editContextWithGate}
            compact={compact}
          />
        </TabsContent>
        <TabsContent value="indexes" className={tabContentClass}>
          <IndexesTab
            structure={structure}
            indexes={indexes}
            testId={`${testId}-indexes`}
            editContext={editContextWithGate}
            compact={compact}
          />
        </TabsContent>
        <TabsContent value="storage" className={tabContentClass}>
          <StorageTab
            testId={`${testId}-storage`}
            editContext={editContextWithGate}
            structure={structure}
            compact={compact}
          />
        </TabsContent>
      </Tabs>
    </DdlEditorShell>
  );
}
